import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import {
  HpiDataTable,
  HpiDateFilterBar,
  HpiPagination,
  HpiStatsBar,
} from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate, parseLocalDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* -- Types ------------------------------------------------ */

interface NewCase {
  id: number
  date: string | null
  investigation_type: string | null
  case_name: string | null
  referral_name: string | null
  referral_customer_id: number | null
  referral_is_individual: boolean
  estimated_billable: number
  status: string
  start_date: string
  end_date: string
}

interface Summary {
  case_count: number
  total_estimated: number
  avg_per_case: number
}

interface Props {
  cases: NewCase[]
  start_date: string
  end_date: string
  summary: Summary
}

/* -- Helpers ---------------------------------------------- */

function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return "-"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

function formatDateRange(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  }
  const s = parseLocalDate(start).toLocaleDateString("en-US", opts)
  const e = parseLocalDate(end).toLocaleDateString("en-US", opts)
  return `${s} - ${e}`
}

/* -- Columns ---------------------------------------------- */

const PER_PAGE = 15

const columns: HpiColumn<NewCase>[] = [
  {
    header: "Date",
    width: "max-w-[110px]",
    render: (row) => (
      <a
        href={`/cases/${row.id}`}
        target="_blank"
        rel="noreferrer"
        className="text-sm text-blue-600 hover:underline"
      >
        {formatDate(row.date)}
      </a>
    ),
  },
  {
    header: "Type",
    width: "max-w-[140px]",
    render: (row) => (
      <a
        href={`/cases/${row.id}`}
        target="_blank"
        rel="noreferrer"
        className="text-sm text-blue-600 hover:underline"
      >
        {row.investigation_type ?? "-"}
      </a>
    ),
  },
  {
    header: "Case",
    render: (row) => (
      <a
        href={`/cases/${row.id}`}
        target="_blank"
        rel="noreferrer"
        className="text-sm text-blue-600 hover:underline"
      >
        {row.case_name ?? "-"}
      </a>
    ),
  },
  {
    header: "Referred By",
    render: (row) => {
      if (row.referral_is_individual) {
        return <div className="text-sm">Individual</div>
      }
      if (row.referral_customer_id) {
        return (
          <a
            href={`/reports/referrals?customer_id=${row.referral_customer_id}&start_date=${row.start_date}&end_date=${row.end_date}`}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-blue-600 hover:underline"
          >
            {row.referral_name ?? "-"}
          </a>
        )
      }
      return <div className="text-sm">{row.referral_name ?? "-"}</div>
    },
  },
  {
    header: "Estimated",
    subHeader: "Billable",
    width: "max-w-[120px]",
    render: (row) => (
      <a
        href={`/cases/${row.id}`}
        target="_blank"
        rel="noreferrer"
        className="text-sm text-blue-600 hover:underline"
      >
        {row.estimated_billable > 0
          ? formatCurrency(row.estimated_billable)
          : ""}
      </a>
    ),
  },
  {
    header: "Status",
    width: "max-w-[100px]",
    render: (row) => (
      <a
        href={`/cases/${row.id}`}
        target="_blank"
        rel="noreferrer"
        className="text-sm text-blue-600 hover:underline"
      >
        {row.status}
      </a>
    ),
  },
]

/* -- Main Component --------------------------------------- */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Reports", href: "/reports/case_activity" },
  { title: "New Cases", href: "/reports/marketing/new_cases" },
]

export default function NewCasesReport({
  cases,
  start_date,
  end_date,
  summary,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [page, setPage] = useState(1)

  const totalPages = Math.ceil(cases.length / PER_PAGE)
  const paginated = cases.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const handleUpdate = () => {
    setPage(1)
    router.get(
      "/reports/marketing/new_cases",
      {
        start_date: startDate,
        end_date: endDate,
      },
      { preserveState: true },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="New Cases Report" />

      <div className="p-6">
        {/* Date Filter Bar */}
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        />

        {/* Data Table */}
        <HpiDataTable<NewCase>
          title={`Case Activity  ${formatDateRange(start_date, end_date)}`}
          columns={columns}
          data={paginated}
          keyFn={(row) => row.id}
          pagination={
            cases.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={cases.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />

        {/* Summary Stats */}
        <HpiStatsBar
          className="mt-6"
          stats={[
            {
              label: "Number of",
              subLabel: "Cases",
              value: summary.case_count,
            },
            {
              label: "Total Estimated",
              subLabel: "Value",
              value: formatCurrency(summary.total_estimated),
            },
            {
              label: "Average",
              subLabel: "Per Case",
              value: formatCurrency(summary.avg_per_case),
            },
          ]}
        />
      </div>
    </AppLayout>
  )
}
